import React, { useEffect, useState } from 'react';
import {
  edrService,
  AffiliateMapRow,
  BuyLicense,
  CollectLog,
  CompanyOption,
} from '../../../services/edr.service';
import { blockReviewer, decimalOnly, toAmount } from './edrAdminGuard';

/**
 * PRD [S10] 관리자 — EDR 라이선스 관리
 *
 * **기간 선택이 없다.** 구매수량은 계약 기준의 현재값 하나이며, 기간을 두면
 * "이번 달 구매수량"이라는 개념이 생겨 매월 다시 입력해야 한다 (SC-9).
 *
 * 수집 상태와 매핑 규칙은 읽기 전용이다. 매핑은 화면 상수가 아니라 API에서 읽는다 —
 * 계열사가 늘 때 배포가 필요하면 안 된다 (§4.1.1).
 */

/** 입력 중에는 문자열로 들고 있는다 — 지웠을 때 0이 강제로 박히면 편집이 불가능해진다 */
type BuyDraft = { companyName: string; pcAmount: string; serverAmount: string };

/**
 * **회사 목록**을 기준으로 행을 만들고 저장값을 병합한다.
 *
 * ⚠️ 저장된 행만 렌더하면 최초 상태(구매수량 0행)에서 입력할 칸이 없어
 * 영원히 입력할 수 없다 — 운영 배포 후 실제로 "등록된 회사가 없습니다"가 떴다.
 * 구매수량은 수기 입력이므로 **입력할 자리를 화면이 먼저 만들어 줘야** 한다.
 * (DLP 라이선스 관리도 같은 방식이다)
 */
const toDraft = (companies: CompanyOption[], saved: BuyLicense[]): BuyDraft[] => {
  const byCompany = new Map(saved.map((r) => [r.companyName, r]));

  return companies.map((c) => {
    const row = byCompany.get(c.companyName);
    return {
      companyName: c.companyName,
      // 미입력과 0을 구분한다 — 빈 칸이면 아직 등록하지 않은 것이다
      pcAmount: row ? String(row.pcAmount) : '',
      serverAmount: row ? String(row.serverAmount) : '',
    };
  });
};

/** 소수 합산이므로 부동소수 오차를 정리한다 — 0.5 단위이니 1자리로 충분하다 */
const sumOf = (rows: BuyDraft[], key: 'pcAmount' | 'serverAmount'): number =>
  Math.round(rows.reduce((acc, r) => acc + toAmount(r[key]), 0) * 10) / 10;

const formatDateTime = (value: string | null): string => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString('ko-KR');
};

/** `0.5`는 공용 계열사 1/2 배분이다. 소수로 보여주면 규칙이 읽히지 않는다 */
const formatWeight = (row: AffiliateMapRow): string => {
  if (row.isExcluded) return '—';
  if (row.weight === 0.5) return '1/2';
  return String(row.weight);
};

const EdrLicenseAdmin: React.FC<{ isReviewer: boolean }> = ({ isReviewer }) => {
  const [draft, setDraft] = useState<BuyDraft[]>([]);
  const [companyCount, setCompanyCount] = useState<number | null>(null);
  const [isBuyLoaded, setIsBuyLoaded] = useState(false);
  const [collectLog, setCollectLog] = useState<CollectLog | null>(null);
  const [isLogLoaded, setIsLogLoaded] = useState(false);
  const [affiliateMap, setAffiliateMap] = useState<AffiliateMapRow[]>([]);

  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    // 회사 목록이 행의 기준이다 — 둘을 함께 받아 병합한다
    Promise.all([edrService.getCompanies(), edrService.getBuyLicenses()])
      .then(([companies, saved]) => {
        setCompanyCount(companies.length);
        setDraft(toDraft(companies, saved));
      })
      .catch(() => setMessage({ type: 'error', text: '구매수량을 불러오지 못했습니다.' }))
      .finally(() => setIsBuyLoaded(true));

    edrService
      .getCollectStatus()
      .then((log) => {
        setCollectLog(log);
        setIsLogLoaded(true);
      })
      .catch(() => setIsLogLoaded(true));

    edrService
      .getAffiliateMap()
      .then(setAffiliateMap)
      .catch(() => setMessage({ type: 'error', text: '매핑 규칙을 불러오지 못했습니다.' }));
  }, []);

  const updateAmount = (
    companyName: string,
    key: 'pcAmount' | 'serverAmount',
    raw: string,
  ) =>
    setDraft((prev) =>
      prev.map((row) =>
        row.companyName === companyName ? { ...row, [key]: decimalOnly(raw) } : row,
      ),
    );

  const handleSave = async () => {
    if (blockReviewer(isReviewer)) return;

    setIsSaving(true);
    try {
      // 전 행을 함께 보낸다 — 백엔드가 회사별 upsert를 한 트랜잭션으로 처리한다
      await edrService.saveBuyLicenses(
        draft.map((row) => ({
          companyName: row.companyName,
          pcAmount: toAmount(row.pcAmount),
          serverAmount: toAmount(row.serverAmount),
        })),
      );
      setMessage({ type: 'success', text: '구매수량을 저장했습니다.' });
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error?.response?.data?.message || '구매수량 저장에 실패했습니다.',
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="edr-license-admin">
      <h2>EDR 라이선스 구매수량 관리</h2>

      {message && (
        <div className={`message ${message.type}`}>
          {message.text}
          <button onClick={() => setMessage(null)}>×</button>
        </div>
      )}

      <p className="edr-hint">
        구매수량은 기간 개념이 없다. 변경이 필요한 행만 수정한 뒤 저장한다.
        한 계약을 두 회사가 반씩 갖는 경우 <strong>0.5</strong>처럼 소수 1자리까지 입력할 수 있다.
      </p>

      <div className="edr-license-admin__buy">
        <div className="table-container">
          <table className="data-table" aria-label="구매수량">
            <thead>
              <tr>
                <th>회사</th>
                <th>PC 구매수량</th>
                <th>서버 구매수량</th>
              </tr>
            </thead>
            <tbody>
              {draft.length === 0 ? (
                <tr>
                  <td colSpan={3} className="no-data">
                    {!isBuyLoaded
                      ? '불러오는 중…'
                      : companyCount === 0
                        ? 'EDR 사용 회사가 없습니다. 회사 설정에서 EDR 사용을 켜주세요.'
                        : '표시할 회사가 없습니다.'}
                  </td>
                </tr>
              ) : (
                draft.map((row) => (
                  <tr key={row.companyName}>
                    <td>{row.companyName}</td>
                    <td>
                      <input
                        className="form-input"
                        type="text"
                        inputMode="decimal"
                        aria-label={`${row.companyName} PC 구매수량`}
                        value={row.pcAmount}
                        disabled={isSaving}
                        onChange={(e) =>
                          updateAmount(row.companyName, 'pcAmount', e.target.value)
                        }
                      />
                    </td>
                    <td>
                      <input
                        className="form-input"
                        type="text"
                        inputMode="decimal"
                        aria-label={`${row.companyName} 서버 구매수량`}
                        value={row.serverAmount}
                        disabled={isSaving}
                        onChange={(e) =>
                          updateAmount(row.companyName, 'serverAmount', e.target.value)
                        }
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            {draft.length > 0 && (
              <tfoot>
                <tr data-testid="edr-buy-total" className="edr-license-admin__total">
                  <td>총합</td>
                  <td>{sumOf(draft, 'pcAmount').toLocaleString()}</td>
                  <td>{sumOf(draft, 'serverAmount').toLocaleString()}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
        <div className="edr-notice-actions edr-notice-actions--end">
          <button
            className="btn-primary"
            onClick={handleSave}
            disabled={isSaving || draft.length === 0}
          >
            구매수량 저장
          </button>
        </div>
      </div>

      {/* ── 수집 상태 (읽기 전용) ───────────────────────────── */}
      <div className="edr-license-admin__collect">
        <h3>
          설치 라이선스 수집 상태 <span className="inline-badge badge-active">읽기 전용</span>
        </h3>

        {!collectLog ? (
          <p className="no-data">
            {isLogLoaded ? '수집 이력이 없습니다.' : '수집 상태를 불러오는 중…'}
          </p>
        ) : (
          <>
            <table className="policy-table" aria-label="설치 라이선스 수집 상태">
              <tbody>
                <tr>
                  <th>최근 수집 일시</th>
                  <td>
                    {formatDateTime(collectLog.finishedAt ?? collectLog.startedAt)}{' '}
                    <span
                      className={`inline-badge ${
                        collectLog.status === 'SUCCESS' ? 'badge-active' : 'badge-parse-partial'
                      }`}
                    >
                      {collectLog.status === 'SUCCESS' ? '성공' : '실패'}
                    </span>
                  </td>
                </tr>
                <tr>
                  <th>대상 기간</th>
                  <td className="mono">{collectLog.period}</td>
                </tr>
                <tr>
                  <th>수집 행 수</th>
                  <td>
                    {collectLog.rowCount === null
                      ? '—'
                      : `${collectLog.rowCount.toLocaleString()}건`}
                  </td>
                </tr>
                <tr>
                  <th>실행 방식</th>
                  <td>{collectLog.triggerType}</td>
                </tr>
              </tbody>
            </table>

            {collectLog.errorMessage && (
              <div className="message error">
                <span>수집 실패: {collectLog.errorMessage}</span>
              </div>
            )}

            {/*
              미매칭 affiliate는 집계에서 조용히 빠진다 — 화면에 띄우지 않으면
              설치 수량이 실제보다 적게 나오는 것을 아무도 눈치채지 못한다.
            */}
            {collectLog.unmatchedAffiliates && (
              <div className="message error">
                <span>매핑되지 않은 affiliate: {collectLog.unmatchedAffiliates}</span>
              </div>
            )}

            {/*
              사용자 결정(2026-08-21): `구분없음` 같은 UNKNOWN 항목은 **경고하지 않는다.**
              EPP 콘솔 자리표시자 행이 매월 같은 경고를 띄우면 담당자가 경고 자체를
              무시하게 된다. 데이터는 계속 적재되며(버리지 않는다) 수집 로그의
              `unknown_agent_types` 컬럼에 원문이 남는다 — 필요할 때 DB로 확인한다.

              PC·서버 전량이 미분류가 되는 사고(EPP DB 규격 변경)는 수집 스크립트가
              Chat으로 🚨 알린다.
            */}
          </>
        )}
      </div>

      {/* ── affiliate 매핑 규칙 (읽기 전용) ────────────────── */}
      <div className="edr-license-admin__map">
        <h3>affiliate → 계열사 매핑 규칙</h3>
        <p className="edr-hint">
          설치 라이선스는 EPP DB의 <code>affiliate</code> 값을 이 규칙으로 환산해 집계한다.
          공용 계열사는 <strong>추가 카운팅이 아니라 분할</strong>이다.
        </p>
        <table className="policy-table" aria-label="affiliate 매핑 규칙">
          <thead>
            <tr>
              <th>affiliate (EPP DB)</th>
              <th>대상 계열사</th>
              <th>비중</th>
            </tr>
          </thead>
          <tbody>
            {affiliateMap.length === 0 ? (
              <tr>
                <td colSpan={3} className="no-data">
                  매핑 규칙이 없습니다.
                </td>
              </tr>
            ) : (
              affiliateMap.map((row) => (
                <tr
                  key={`${row.affiliate}-${row.companyName}`}
                  className={row.weight !== 1 && !row.isExcluded ? 'edr-map-shared' : undefined}
                >
                  <td className="mono">{row.affiliate}</td>
                  <td>
                    {row.isExcluded ? (
                      <span className="text-muted">대상 아님</span>
                    ) : (
                      row.companyName
                    )}
                  </td>
                  <td>{formatWeight(row)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default EdrLicenseAdmin;
